/**
 * OnePagerDocumentV2.tsx — expanded PDF renderer (DRAFT — not yet wired into the app).
 *
 * Renders an OnePagerV2Model into a US-Letter PDF with the full site/block/block-group/tract
 * layout. All real logic lives in onePagerModelV2.ts; this file only maps data → react-pdf.
 */
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer'
import type { OnePagerV2Model, LabeledRow } from '../../lib/pdf/onePagerModelV2'
import { GEO_LEVEL_LABELS } from '../../lib/geoLevel'
import brandLogo from '@/assets/OptiYield_logo.png'

const TEAL = '#45b4ac'
const INK = '#1a1a1a'
const GRAY = '#666666'
const LINE = '#e2e2e2'
const TEAL_TINT = '#e0f5f4'
const LEVEL_GRAY = '#949494'
const DOMINANT_BG = '#fffbeb' // light amber highlight for dominant group

const s = StyleSheet.create({
  page: { padding: '24pt 32pt 20pt', fontSize: 9, color: INK, fontFamily: 'Helvetica' },

  // Header
  hdr: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 2.5,
    borderBottomColor: TEAL,
    paddingBottom: 8,
  },
  brand: { flexDirection: 'row', alignItems: 'center' },
  brandLogo: { height: 32, width: 'auto' as const },
  addrBox: { alignItems: 'flex-end' },
  addr1: { fontSize: 10, fontFamily: 'Helvetica-Bold' },
  addrApprox: { fontSize: 7, color: GRAY, marginTop: 1 },
  meta: { fontSize: 8, color: GRAY, marginTop: 2 },

  // Hero
  hero: { flexDirection: 'row', alignItems: 'center', marginTop: 12 },
  heroNum: { fontSize: 44, fontFamily: 'Helvetica-Bold' },
  heroBody: { marginLeft: 14, flexGrow: 1 },
  heroLabel: { fontSize: 13, fontFamily: 'Helvetica-Bold' },
  heroDesc: { fontSize: 8, color: GRAY, marginTop: 2, maxWidth: 320 },
  heroBaseline: { fontSize: 8, color: GRAY, marginTop: 3 },
  heroBaselineVal: { fontFamily: 'Helvetica-Bold', color: INK },
  heroPending: { fontSize: 8, color: GRAY, marginTop: 3 },

  // Classification
  clsSection: { marginTop: 8 },
  clsRow: { flexDirection: 'row', alignItems: 'center' },
  clsEyebrow: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: TEAL,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginRight: 8,
  },
  clsBadge: {
    flexDirection: 'row',
    alignItems: 'baseline',
    backgroundColor: TEAL_TINT,
    borderRadius: 4,
    padding: '1pt 6pt',
  },
  clsBadgeLabel: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: INK },
  clsBadgeScore: { fontSize: 7.5, color: GRAY, marginLeft: 6 },
  clsBadgeScoreVal: { fontFamily: 'Helvetica-Bold', color: INK },
  clsSentence: { fontSize: 7, color: GRAY, marginTop: 2, lineHeight: 1.2, maxWidth: 520 },
  clsNote: { fontSize: 7, color: GRAY, marginTop: 2 },

  // Section heading
  h2: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: TEAL,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginTop: 12,
    marginBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: LINE,
    paddingBottom: 3,
  },
  // Sub-heading within a section
  h3: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: '#444444',
    marginTop: 4,
    marginBottom: 2,
  },

  // Sub-score cards
  cards: { flexDirection: 'row', justifyContent: 'space-between' },
  card: {
    width: '23.5%',
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 5,
    padding: '6pt 7pt',
    flexDirection: 'column',
  },
  cardLbl: { fontSize: 7.5, color: GRAY, minHeight: 10 },
  cardLvl: { fontSize: 6.5, color: LEVEL_GRAY },
  cardVal: { fontSize: 18, fontFamily: 'Helvetica-Bold', marginTop: 1 },
  cardPct: { fontSize: 6.5, color: LEVEL_GRAY, marginTop: 'auto' },

  // Data rows
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 0.5,
    borderBottomColor: LINE,
    paddingVertical: 2,
  },
  rowK: { color: GRAY, fontSize: 8.5, flexShrink: 0, maxWidth: '70%' },
  rowV: { fontFamily: 'Helvetica-Bold', fontSize: 8.5, textAlign: 'right' as const },
  rowLvl: { color: LEVEL_GRAY, fontSize: 6.5 },

  // Two-column layout
  two: { flexDirection: 'row', justifyContent: 'space-between' },
  col: { width: '48%' },

  // Tract metric group
  groupBox: { marginTop: 2, marginBottom: 2 },
  groupBoxDominant: {
    marginTop: 2,
    marginBottom: 2,
    backgroundColor: DOMINANT_BG,
    padding: '2pt 4pt',
    borderRadius: 3,
  },
  groupTitle: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: INK, marginBottom: 2 },
  groupTitleDominant: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: TEAL, marginBottom: 2 },
  starLabel: { fontSize: 7, color: TEAL },

  // Section wrapper — used with wrap={false} to prevent page breaks within
  section: {},

  // Nearby competition table
  nearbyTable: { marginTop: 10 },
  nearbyHdr: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: LINE,
    paddingBottom: 2,
  },
  nearbyHdrCell: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: GRAY,
    textAlign: 'right' as const,
  },
  nearbyRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: LINE,
    paddingVertical: 1.5,
  },
  nearbyLabel: { fontSize: 8, color: GRAY, width: '45%' },
  nearbyVal: { fontSize: 8, fontFamily: 'Helvetica-Bold', textAlign: 'right' as const },
  nearbyLine: { fontSize: 7.5, color: GRAY, marginTop: 1.5, lineHeight: 1.2 },
  nearbyFootnote: { fontSize: 7, color: GRAY, marginTop: 2, fontStyle: 'italic' as const },

  // Amenities
  amenityGrid: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 2 },
  amenityItem: { width: '33%', fontSize: 8, paddingVertical: 1 },
  amenityLabel: { color: GRAY },
  amenityVal: { fontFamily: 'Helvetica-Bold' },

  // Glossary — two-column layout
  glossaryColumns: { flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap' },
  glossaryItem: {
    width: '48%',
    marginBottom: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: LINE,
    paddingBottom: 1.5,
  },
  glossaryTerm: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: INK },
  glossaryDef: { fontSize: 6.5, color: GRAY, marginTop: 0.5, lineHeight: 1.2 },
  glossaryInputs: { fontSize: 6, color: LEVEL_GRAY, marginTop: 0.5, lineHeight: 1.2 },
  glossaryRange: { fontSize: 6, color: LEVEL_GRAY, marginTop: 0.5 },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 16,
    left: 32,
    right: 32,
    fontSize: 7,
    color: GRAY,
    lineHeight: 1.3,
  },
  footerMethod: { flexDirection: 'row' },
})

// ─── Components ──────────────────────────────────────────────────────────────

function DataRow({ label, value, level }: LabeledRow) {
  return (
    <View style={s.row}>
      <Text style={s.rowK}>
        {label} <Text style={s.rowLvl}> {GEO_LEVEL_LABELS[level]}</Text>
      </Text>
      <Text style={s.rowV}>{value}</Text>
    </View>
  )
}

function DataRows({ rows }: { rows: LabeledRow[] }) {
  return (
    <>
      {rows.map((r, i) => (
        <DataRow key={i} {...r} />
      ))}
    </>
  )
}

export function OnePagerDocumentV2({ model: m }: { model: OnePagerV2Model }) {
  const colWidth = m.siteDetails.nearby.available
    ? `${(100 - 45) / m.siteDetails.nearby.columns.length}%`
    : '25%'

  return (
    <Document>
      <Page size="LETTER" style={s.page}>
        {/* ── Header ── */}
        <View style={s.hdr}>
          <View style={s.brand}>
            <Image src={brandLogo} style={s.brandLogo} />
          </View>
          <View style={s.addrBox}>
            {m.header.address && <Text style={s.addr1}>{m.header.address.toUpperCase()}</Text>}
            {m.header.addressApproximate && (
              <Text style={s.addrApprox}>approximate — based on map location</Text>
            )}
            <Text style={s.meta}>
              {m.header.tractLabel} · {m.header.stateName}
            </Text>
            <Text style={s.meta}>{m.header.vintageLabel}</Text>
          </View>
        </View>

        {/* ── Hero ── */}
        <View style={s.hero}>
          <Text style={[s.heroNum, { color: m.hero.scoreColor }]}>{Math.round(m.hero.score)}</Text>
          <View style={s.heroBody}>
            <Text style={s.heroLabel}>{m.hero.label}</Text>
            <Text style={s.heroDesc}>{m.hero.description}</Text>
            {m.hero.tractBaseline && (
              <Text style={s.heroBaseline}>
                {m.hero.tractBaseline.label}:{' '}
                <Text style={s.heroBaselineVal}>{m.hero.tractBaseline.value}</Text>
              </Text>
            )}
            {m.hero.degradedNote && <Text style={s.heroPending}>{m.hero.degradedNote}</Text>}
            {m.hero.statePercentileNote && (
              <Text style={s.heroBaseline}>{m.hero.statePercentileNote}</Text>
            )}
          </View>
        </View>

        {/* ── Classification ── */}
        {m.classification.available && (
          <View style={s.clsSection}>
            <View style={s.clsRow}>
              <Text style={s.clsEyebrow}>Location Classification</Text>
              {m.classification.badge && (
                <View style={s.clsBadge}>
                  <Text style={s.clsBadgeLabel}>{m.classification.badge.label}</Text>
                  {m.classification.badge.scoreText && (
                    <Text style={s.clsBadgeScore}>
                      {m.classification.badge.scoreLabel}{' '}
                      <Text style={s.clsBadgeScoreVal}>{m.classification.badge.scoreText}</Text>
                      {m.classification.badge.level && (
                        <Text style={s.rowLvl}>
                          {' '}
                          {GEO_LEVEL_LABELS[m.classification.badge.level]}
                        </Text>
                      )}
                    </Text>
                  )}
                </View>
              )}
            </View>
            {m.classification.sentences.map((sent, i) => (
              <Text key={i} style={s.clsSentence}>
                {sent}
              </Text>
            ))}
          </View>
        )}
        {!m.classification.available && m.classification.note && (
          <View style={s.clsSection}>
            <View style={s.clsRow}>
              <Text style={s.clsEyebrow}>Location Classification</Text>
              <Text style={s.clsNote}>{m.classification.note}</Text>
            </View>
          </View>
        )}

        {/* ── Sub-Scores ── */}
        <Text style={s.h2}>Site Sub-Scores</Text>
        <View style={s.cards}>
          {m.subScores.map((sc, i) => (
            <View key={i} style={s.card}>
              <Text style={s.cardLbl}>
                {sc.label} <Text style={s.cardLvl}>{GEO_LEVEL_LABELS[sc.level]}</Text>
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Text style={[s.cardVal, { color: sc.valueColor }]}>{sc.value}</Text>
                {sc.sublabel && <Text style={[s.cardLvl, { marginLeft: 3 }]}>{sc.sublabel}</Text>}
              </View>
              {sc.percentileNote && <Text style={s.cardPct}>{sc.percentileNote}</Text>}
            </View>
          ))}
        </View>

        {/* ── Site Details & Nearby ── */}
        {m.siteDetails.available && (
          <>
            <Text style={s.h2}>Site Details & Nearby</Text>
            <View style={s.two}>
              {/* Left: site inputs */}
              <View style={s.col}>
                {m.siteDetails.inputs?.map((inp, i) => (
                  <View key={i} style={s.row}>
                    <Text style={s.rowK}>{inp.label}</Text>
                    <Text style={s.rowV}>{inp.value}</Text>
                  </View>
                ))}
              </View>
              {/* Right: amenities */}
              <View style={s.col}>
                {m.siteDetails.nearby.available &&
                  m.siteDetails.nearby.amenities.rows.length > 0 && (
                    <>
                      <Text style={s.h3}>
                        Amenities within {m.siteDetails.nearby.amenities.radiusMi} mi
                      </Text>
                      {m.siteDetails.nearby.amenities.rows.map((a, i) => (
                        <View key={i} style={s.row}>
                          <Text style={s.rowK}>{a.label}</Text>
                          <Text style={s.rowV}>{a.value}</Text>
                        </View>
                      ))}
                    </>
                  )}
              </View>
            </View>
            {/* Nearby charger table */}
            {m.siteDetails.nearby.available && (
              <View style={s.nearbyTable}>
                {/* Header row with title inline */}
                <View style={s.nearbyHdr}>
                  <Text
                    style={[
                      s.nearbyLabel,
                      { fontSize: 8, fontFamily: 'Helvetica-Bold', color: GRAY },
                    ]}
                  >
                    Nearby Chargers
                  </Text>
                  {m.siteDetails.nearby.columns.map((col, i) => (
                    <Text key={i} style={[s.nearbyHdrCell, { width: colWidth }]}>
                      {col}
                    </Text>
                  ))}
                </View>
                {/* Data rows */}
                {m.siteDetails.nearby.competitionRows.map((cr, i) => (
                  <View key={i} style={s.nearbyRow}>
                    <Text style={s.nearbyLabel}>{cr.label}</Text>
                    {cr.values.map((v, j) => (
                      <Text key={j} style={[s.nearbyVal, { width: colWidth }]}>
                        {v}
                      </Text>
                    ))}
                  </View>
                ))}
                {/* Prose lines */}
                {m.siteDetails.nearby.lines.map((line, i) => (
                  <Text key={i} style={s.nearbyLine}>
                    • {line}
                  </Text>
                ))}
                {/* Footnotes */}
                {m.siteDetails.nearby.footnotes.map((fn, i) => (
                  <Text key={i} style={s.nearbyFootnote}>
                    {fn}
                  </Text>
                ))}
              </View>
            )}
          </>
        )}

        {/* ── Block + Block Group side by side ── */}
        {(m.blockData.available || m.blockGroupData.available) && (
          <>
            <Text style={s.h2}>Block & Block Group Data</Text>
            <View style={s.two}>
              {/* Left column: Block */}
              <View style={s.col}>
                {m.blockData.available && (
                  <>
                    <Text style={s.h3}>Block Scores</Text>
                    <DataRows rows={m.blockData.scores} />
                    {m.blockData.retailInputs.length > 0 && (
                      <>
                        <Text style={s.h3}>Retail & Dwell Inputs</Text>
                        <DataRows rows={m.blockData.retailInputs} />
                      </>
                    )}
                    {m.blockData.context.length > 0 && (
                      <>
                        <Text style={s.h3}>Block Context</Text>
                        <DataRows rows={m.blockData.context} />
                      </>
                    )}
                  </>
                )}
              </View>
              {/* Right column: Block Group */}
              <View style={s.col}>
                {m.blockGroupData.available && (
                  <>
                    <Text style={s.h3}>Block Group Scores</Text>
                    <DataRows rows={m.blockGroupData.scores} />
                    {m.blockGroupData.hccInputs.length > 0 && (
                      <>
                        <Text style={s.h3}>HCC Inputs</Text>
                        <DataRows rows={m.blockGroupData.hccInputs} />
                      </>
                    )}
                    {m.blockGroupData.hcoInputs.length > 0 && (
                      <>
                        <Text style={s.h3}>HCO Inputs</Text>
                        <DataRows rows={m.blockGroupData.hcoInputs} />
                      </>
                    )}
                    {m.blockGroupData.context.length > 0 && (
                      <>
                        <Text style={s.h3}>Block Group Context</Text>
                        <DataRows rows={m.blockGroupData.context} />
                      </>
                    )}
                  </>
                )}
              </View>
            </View>
          </>
        )}

        {/* ── Tract Metrics in two columns, dominant highlighted ── */}
        <Text style={s.h2}>Tract Metrics</Text>
        <View style={s.two}>
          <View style={s.col}>
            {m.tractMetrics.slice(0, 3).map((group) => (
              <View key={group.key} style={group.isDominant ? s.groupBoxDominant : s.groupBox}>
                <Text style={group.isDominant ? s.groupTitleDominant : s.groupTitle}>
                  {group.isDominant && <Text style={s.starLabel}>★ </Text>}
                  {group.title}
                </Text>
                {group.rows.map((r, i) => (
                  <DataRow key={i} {...r} />
                ))}
              </View>
            ))}
          </View>
          <View style={s.col}>
            {m.tractMetrics.slice(3).map((group) => (
              <View key={group.key} style={group.isDominant ? s.groupBoxDominant : s.groupBox}>
                <Text style={group.isDominant ? s.groupTitleDominant : s.groupTitle}>
                  {group.isDominant && <Text style={s.starLabel}>★ </Text>}
                  {group.title}
                </Text>
                {group.rows.map((r, i) => (
                  <DataRow key={i} {...r} />
                ))}
              </View>
            ))}
          </View>
        </View>

        {/* ── Glossary ── */}
        <Text style={s.h2}>Score Definitions</Text>
        <View style={s.glossaryColumns}>
          {m.glossary.map((g, i) => (
            <View key={i} style={s.glossaryItem}>
              <Text style={s.glossaryTerm}>{g.term}</Text>
              <Text style={s.glossaryDef}>{g.definition}</Text>
              {g.inputs && <Text style={s.glossaryInputs}>Inputs: {g.inputs}</Text>}
              <Text style={s.glossaryRange}>Range: {g.range}</Text>
            </View>
          ))}
        </View>

        {/* ── Footer ── */}
        <View style={s.footer}>
          <Text>{m.footer.disclaimer}</Text>
          <View style={s.footerMethod}>
            <Text> · Methodology: </Text>
            <Text style={{ fontFamily: 'Helvetica-Bold' }}>{m.footer.methodologyUrl}</Text>
          </View>
        </View>
      </Page>
    </Document>
  )
}
